import { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getSchema, getSchemas } from '../api/schemas'
import { getProjectDevices, getAllProjectConnections } from '../api/deviceInstances'
import { getCableToTerminalConnections } from '../api/connections'
import {
  getDeviceContacts as fetchDeviceContacts,
  getTerminalInternalConnections,
  type DeviceContact as ApiDeviceContact,
  type TerminalInternalConnection,
} from '../api/deviceContacts'
import type { DeviceInstance } from '../types'
import type { ElectricConnection } from '../types/connections'

interface KnxCsvRow {
  area: string
  range: string
  channel: string
  groupAddress: string
  description: string
  comment: string
  extra: string
  datapoint: string
  mode: string
}

interface ColumnDefinition {
  key: keyof KnxCsvRow
  label: string
  align?: 'left' | 'center' | 'right'
  monospace?: boolean
}

interface RelayDefinition {
  relayLabel: string
  area: string
  line: string
  channelCount: number
}

interface RelayChannelMappingResult {
  channelNameMap: Record<string, string>
  channelDetailsMap: Record<string, ChannelDetails>
  relayDefinitions: RelayDefinition[]
  debugRows: RelayConnectionDebugRow[]
  bottomContacts: RelayBottomContactRow[]
}
interface RelayConnectionDebugRow {
  connectionId: number
  schemaId: number
  relayInstanceId: number
  relayContactId: number
  relayLabel: string | null
  relayContactLabel: string | null
  terminalInstanceId: number
  terminalContactId: number
  terminalLabel: string | null
  terminalContactLabel: string | null
}

interface RelayBottomContactRow {
  relayInstanceId: number
  relayLabel: string | null
  relayContactId: number
  relayContactLabel: string | null
  connectionId: number | null
  schemaId: number | null
  targetInstanceId: number | null
  targetContactId: number | null
  targetLabel: string | null
  targetContactLabel: string | null
  oppositeContactId: number | null
  oppositeContactLabel: string | null
  terminalSpecId: number | null
  hasInternalMap: boolean
  cableConnectionId: number | null
  cableInstanceId: number | null
  cableContactId: number | null
  cableContactLabel: string | null
  cableColor: string | null
  cableOppositeConnectionId: number | null
  cableOppositeInstanceId: number | null
  cableOppositeContactId: number | null
  cableOppositeContactLabel: string | null
  cableOppositeLabel: string | null
  targetSpecName: string | null
  targetDescription: string | null
  cableOppositeSpecName: string | null
  cableOppositeDescription: string | null
}

interface ChannelDetails {
  specName: string | null
  deviceDescription: string | null
}

interface ResolvedChannelInfo {
  label: string
  specName: string | null
  deviceDescription: string | null
}

function formatChannelDescription(details?: ChannelDetails): string {
  if (!details) return ''
  const spec = details.specName?.trim()
  const desc = details.deviceDescription?.trim()
  if (spec && desc) {
    return `${spec}. ${desc}`
  }
  if (spec) {
    return spec
  }
  if (desc) {
    return desc
  }
  return ''
}

const KNX_COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { key: 'area', label: 'Area' },
  { key: 'range', label: 'Range' },
  { key: 'channel', label: 'Channel' },
  { key: 'groupAddress', label: 'Group Address', monospace: true },
  { key: 'description', label: 'Description' },
  { key: 'comment', label: 'Comment' },
  { key: 'extra', label: 'Extra' },
  { key: 'datapoint', label: 'Datapoint' },
  { key: 'mode', label: 'Mode' },
]

const DEFAULT_CHANNEL_COUNT = 16
const FEEDBACK_OFFSET = 100

type DeviceContact = ApiDeviceContact & { contact_number: string | number }

const WINDOWS1251_EXTRA_CHARS: Record<number, number> = {
  0x0401: 0xa8,
  0x0451: 0xb8,
  0x0404: 0xaa,
  0x0454: 0xba,
  0x0406: 0xb2,
  0x0456: 0xb3,
  0x0407: 0xaf,
  0x0457: 0xbf,
  0x040e: 0xa1,
  0x045e: 0xa2,
  0x0490: 0xa5,
  0x0491: 0xb4,
  0x2116: 0xb9,
  0x00ab: 0xab,
  0x00bb: 0xbb,
  0x00b0: 0xb0,
  0x00b1: 0xb1,
  0x00b5: 0xb5,
  0x00b7: 0xb7,
  0x2013: 0x96,
  0x2014: 0x97,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201a: 0x82,
  0x201c: 0x93,
  0x201d: 0x94,
  0x201e: 0x84,
  0x2020: 0x86,
  0x2021: 0x87,
  0x2022: 0x95,
  0x2026: 0x85,
  0x2030: 0x89,
  0x2039: 0x8b,
  0x203a: 0x9b,
  0x20ac: 0x88,
}

function toArray<T>(value: any): T[] {
  if (Array.isArray(value)) {
    return value
  }
  if (value && Array.isArray(value.data)) {
    return value.data
  }
  return []
}

function normaliseContactNumber(contactNumber: string | number | null | undefined): string | null {
  if (contactNumber === null || contactNumber === undefined) return null
  if (typeof contactNumber === 'number') return contactNumber.toString()
  return contactNumber
}

function getContactBase(contactNumber: string | number | null | undefined): string | null {
  const normalized = normaliseContactNumber(contactNumber)
  if (!normalized) return null
  return normalized.split('-')[0]
}

function buildCableContactPairMap(contacts: DeviceContact[]): Map<number, number> {
  const result = new Map<number, number>()
  const groups = new Map<string, DeviceContact[]>()

  contacts.forEach((contact) => {
    const base = getContactBase(contact.contact_number)
    if (!base) return
    const group = groups.get(base) ?? []
    group.push(contact)
    groups.set(base, group)
  })

  groups.forEach((group) => {
    if (group.length < 2) return
    group.forEach((contact) => {
      const partner = group.find((candidate) => candidate.id !== contact.id)
      if (partner) {
        result.set(contact.id, partner.id)
      }
    }) ?? 0
  })

  return result
}

function buildKnxRows(
  relays: RelayDefinition[],
  channelNameMap: Record<string, string>,
  channelDetailsMap: Record<string, ChannelDetails>,
): KnxCsvRow[] {
  const rows: KnxCsvRow[] = []

  relays.forEach(({ relayLabel, area, line, channelCount }) => {
    for (let channel = 1; channel <= channelCount; channel += 1) {
      const key = `${relayLabel}.CMD.${channel}`
      const friendly = channelNameMap[key]
      const descriptionText = formatChannelDescription(channelDetailsMap[key])
      rows.push({
        area,
        range: relayLabel,
        channel: friendly ? `${key}. ${friendly}` : key,
        groupAddress: `${area}/${line}/${channel}`,
        description: descriptionText,
        comment: '',
        extra: '',
        datapoint: 'DPST-1-1',
        mode: 'Auto',
      })
    }

    for (let channel = 1; channel <= channelCount; channel += 1) {
      const key = `${relayLabel}.FB.${channel}`
      const friendly = channelNameMap[key]
      const descriptionText = formatChannelDescription(channelDetailsMap[key])
      rows.push({
        area,
        range: relayLabel,
        channel: friendly ? `${key}. ${friendly}` : key,
        groupAddress: `${area}/${line}/${FEEDBACK_OFFSET + channel}`,
        description: descriptionText,
        comment: '',
        extra: '',
        datapoint: 'DPST-1-1',
        mode: 'Auto',
      })
    }
  })

  return rows
}

function toCsvValue(value: string | number | null | undefined, forceQuotes = false): string {
  if (value === null || value === undefined) {
    return ''
  }
  const str = String(value)
  const needsQuotes = forceQuotes || /[;"\n\r]/.test(str)
  const escaped = str.replace(/"/g, '""')
  return needsQuotes ? `"${escaped}"` : escaped
}

function encodeWindows1251(text: string): Uint8Array {
  const bytes: number[] = []
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i)
    if (code <= 0x7f) {
      bytes.push(code)
      continue
    }
    if (code >= 0x0410 && code <= 0x044f) {
      // Cyrillic block А-я
      const offset = code >= 0x0430 ? 0xe0 : 0xc0
      const start = code >= 0x0430 ? 0x0430 : 0x0410
      bytes.push(offset + (code - start))
      continue
    }
    const mapped = WINDOWS1251_EXTRA_CHARS[code]
    if (mapped !== undefined) {
      bytes.push(mapped)
      continue
    }
    // unsupported character → '?'
    bytes.push(0x3f)
  }
  return new Uint8Array(bytes)
}

function resolveRelayChannelName(
  relayLabel: string,
  channelNumber: number,
  context: {
    devicesById: Map<number, DeviceInstance>
    devicesByLabel: Map<string, DeviceInstance>
    connectionsByInstance: Map<number, ElectricConnection[]>
    contactsBySpec: Map<number, DeviceContact[]>
    contactById: Map<number, DeviceContact>
    terminalInternalMaps: Map<number, Map<number, number>>
    terminalInstanceIds: Set<number>
    mainSchemaId: number
    cableSpecPairs: Map<number, Map<number, number>>
    cableColorsBySpec: Map<number, Map<number, string | null>>
  },
): ResolvedChannelInfo | null {
  const relayDevice = context.devicesByLabel.get(relayLabel)
  if (!relayDevice) return null

  const relayContacts = context.contactsBySpec.get(relayDevice.specification_id) ?? []
  const outputLabel = `${channelNumber}o`
  const feedbackLabel = `${channelNumber}i`

  const relayContact =
    relayContacts.find((contact) => contact.contact_label?.toLowerCase() === outputLabel.toLowerCase()) ??
    relayContacts.find((contact) => contact.contact_label?.toLowerCase() === feedbackLabel.toLowerCase())

  if (!relayContact) return null

  const relayConnections =
    context.connectionsByInstance.get(relayDevice.id)?.filter(
      (connection) =>
        connection.schema_id === context.mainSchemaId &&
        ((connection.from_instance_id === relayDevice.id && connection.from_contact_id === relayContact.id) ||
          (connection.to_instance_id === relayDevice.id && connection.to_contact_id === relayContact.id)),
    ) ?? []

  let terminalInstanceId: number | null = null
  let terminalContactId: number | null = null
  let alternateTerminalContactId: number | null = null

  for (const connection of relayConnections) {
    const isFromRelay = connection.from_instance_id === relayDevice.id
    const candidateTerminalId = isFromRelay ? connection.to_instance_id : connection.from_instance_id
    if (!context.terminalInstanceIds.has(candidateTerminalId)) {
      continue
    }

    terminalInstanceId = candidateTerminalId
    terminalContactId = isFromRelay ? connection.to_contact_id : connection.from_contact_id
    const terminalDevice = context.devicesById.get(terminalInstanceId)
    const terminalSpecId = terminalDevice?.specification_id ?? 0
    const internalMap = context.terminalInternalMaps.get(terminalSpecId)
    alternateTerminalContactId = internalMap?.get(terminalContactId) ?? null
    break
  }

  if (terminalInstanceId === null || terminalContactId === null) {
    return null
  }

  const terminalConnections = context.connectionsByInstance.get(terminalInstanceId) ?? []
  const cableConnection = terminalConnections.find((connection) => {
    const isFromTerminal = connection.from_instance_id === terminalInstanceId
    const contactId = isFromTerminal ? connection.from_contact_id : connection.to_contact_id
    if (
      contactId !== terminalContactId &&
      (!alternateTerminalContactId || contactId !== alternateTerminalContactId)
    ) {
      return false
    }
    const otherInstanceId = isFromTerminal ? connection.to_instance_id : connection.from_instance_id
    if (otherInstanceId === relayDevice.id) {
      return false
    }
    const otherDevice = context.devicesById.get(otherInstanceId)
    return otherDevice?.device_type === 'cable'
  })

  if (!cableConnection) {
    return null
  }

  const isFromTerminal = cableConnection.from_instance_id === terminalInstanceId
  const cableInstanceId = isFromTerminal ? cableConnection.to_instance_id : cableConnection.from_instance_id
  const cableDevice = context.devicesById.get(cableInstanceId)
  if (!cableDevice || cableDevice.device_type !== 'cable') {
    return null
  }

  const cableSpecId = cableDevice.specification_id
  const cableContacts = context.contactsBySpec.get(cableSpecId) ?? []
  let cablePairMap = context.cableSpecPairs.get(cableDevice.specification_id)
  if (!cablePairMap) {
    cablePairMap = buildCableContactPairMap(cableContacts)
    context.cableSpecPairs.set(cableDevice.specification_id, cablePairMap)
  }

  const cableContactId = isFromTerminal ? cableConnection.to_contact_id : cableConnection.from_contact_id
  const cableColorMap = context.cableColorsBySpec.get(cableSpecId) ?? null
  const cableContactColor = cableColorMap?.get(cableContactId) ?? null

  let matchingCableContactId = cablePairMap.get(cableContactId) ?? null

  if (!matchingCableContactId && cableContactColor) {
    const alternate = cableContacts.find((contact) => {
      const contactId = Number((contact as any).id ?? contact.id)
      if (!Number.isFinite(contactId) || contactId === cableContactId) {
        return false
      }
      return (contact.color_code ?? null) === cableContactColor
    })
    if (alternate) {
      const alternateId = Number((alternate as any).id ?? alternate.id)
      if (Number.isFinite(alternateId)) {
        matchingCableContactId = alternateId
      }
    }
  }

  const cableConnections = context.connectionsByInstance.get(cableInstanceId) ?? []
  const finalConnection = cableConnections.find((connection) => {
    if (connection.id === cableConnection.id) {
      return false
    }
    const fromCable = connection.from_instance_id === cableInstanceId
    const contactUsed = fromCable ? connection.from_contact_id : connection.to_contact_id
    if (contactUsed === cableContactId) {
      return false
    }
    if (matchingCableContactId && contactUsed !== matchingCableContactId) {
      return false
    }
    if (!matchingCableContactId && cableContactColor) {
      const contactColor = cableColorMap?.get(contactUsed) ?? null
      if (contactColor !== cableContactColor) {
        return false
      }
    }
    const otherInstanceId = fromCable ? connection.to_instance_id : connection.from_instance_id
    if (otherInstanceId === terminalInstanceId || otherInstanceId === relayDevice.id) {
      return false
    }
    const otherDevice = context.devicesById.get(otherInstanceId)
    if (!otherDevice) return false
    if (context.terminalInstanceIds.has(otherInstanceId)) return false
    if (otherDevice.device_type === 'cable') return false
    return Boolean(otherDevice.position_label || otherDevice.description)
  })

  if (!finalConnection) {
    return null
  }

  const finalDeviceId =
    finalConnection.from_instance_id === cableInstanceId
      ? finalConnection.to_instance_id
      : finalConnection.from_instance_id
  const finalDevice = context.devicesById.get(finalDeviceId)

  if (!finalDevice) {
    return null
  }

  const specName = finalDevice.spec_name ?? null
  const deviceDescription = finalDevice.description ?? null
  const label =
    finalDevice.position_label?.trim() ??
    deviceDescription?.trim() ??
    specName?.trim() ??
    null

  if (!label) {
    return null
  }

  return {
    label,
    specName,
    deviceDescription,
  }
}

async function loadRelayChannelMappings(
  projectId: number,
  schemaId: number,
): Promise<RelayChannelMappingResult> {
  const channelNameMap: Record<string, string> = {}
  const channelDetailsMap: Record<string, ChannelDetails> = {}

  if (!projectId || !schemaId) {
    return { channelNameMap, channelDetailsMap, relayDefinitions: [], debugRows: [], bottomContacts: [] }
  }

  const schema = await getSchema(schemaId)
  const panelId = (schema as any)?.electrical_panel_id ?? null
  if (!panelId) {
    return { channelNameMap, channelDetailsMap, relayDefinitions: [], debugRows: [], bottomContacts: [] }
  }

  const schemasResponse = await getSchemas(projectId, panelId)
  const schemas = toArray<any>(schemasResponse)
  const mainSchema = schemas.find((item) => item.schema_type === 'main')
  const mainSchemaId = mainSchema?.id ?? null
  if (!mainSchemaId) {
    return { channelNameMap, channelDetailsMap, relayDefinitions: [], debugRows: [], bottomContacts: [] }
  }

  const [devicesResp, connectionsResp, cableConnectionsResp] = await Promise.all([
    getProjectDevices(projectId, panelId),
    getAllProjectConnections(projectId),
    getCableToTerminalConnections(mainSchemaId),
  ])

  const devices = toArray<DeviceInstance>(devicesResp)
  const connections = toArray<ElectricConnection>(connectionsResp)
  const cableConnectionsRaw = toArray<any>(cableConnectionsResp)

  if (!devices.length || !connections.length) {
    return { channelNameMap, channelDetailsMap, relayDefinitions: [], debugRows: [], bottomContacts: [] }
  }

  const devicesById = new Map<number, DeviceInstance>()
  const devicesByLabel = new Map<string, DeviceInstance>()

  devices.forEach((device) => {
    devicesById.set(device.id, device)
    if (device.position_label) {
      devicesByLabel.set(device.position_label, device)
    }
  })

  const relayDevices = devices.filter(
    (device) => device.position_label && device.position_label.startsWith('R-'),
  )

  if (!relayDevices.length) {
    return { channelNameMap, channelDetailsMap, relayDefinitions: [], debugRows: [], bottomContacts: [] }
  }

  const terminalInstanceIds = new Set<number>()
  cableConnectionsRaw.forEach((item: any) => {
    if (item.terminal_instance_id != null) {
      terminalInstanceIds.add(Number(item.terminal_instance_id))
    }
  })

  const contactsBySpec = new Map<number, DeviceContact[]>()
  const contactById = new Map<number, DeviceContact>()
  const terminalInternalMaps = new Map<number, Map<number, number>>()
  const cableSpecPairs = new Map<number, Map<number, number>>()
  const terminalOpposites = new Map<number, number>()
  const cableColorsBySpec = new Map<number, Map<number, string | null>>()

  const relaySpecIds = new Set(relayDevices.map((device) => device.specification_id))
  const terminalSpecIds = new Set<number>()
  devices.forEach((device) => {
    if (device.device_type === 'terminal') {
      terminalSpecIds.add(device.specification_id)
    }
  })
  const cableSpecIds = new Set<number>()
  devices.forEach((device) => {
    if (
      device.device_type === 'cable' ||
      (device as any).device_type_code === 'cable' ||
      (device as any).device_type_name?.toLowerCase?.().includes('cable')
    ) {
      cableSpecIds.add(device.specification_id)
    }
  })

  cableConnectionsRaw.forEach((item: any) => {
    const terminalSpecId = item.terminal_specification_id != null ? Number(item.terminal_specification_id) : null
    if (terminalSpecId) {
      terminalSpecIds.add(terminalSpecId)
    }
  })

  terminalInstanceIds.forEach((instanceId) => {
    const device = devicesById.get(instanceId)
    if (device?.specification_id) {
      terminalSpecIds.add(device.specification_id)
    }
  })

  connections.forEach((connection) => {
    const fromDevice = devicesById.get(connection.from_instance_id)
    if (fromDevice?.device_type === 'cable') {
      cableSpecIds.add(fromDevice.specification_id)
    }
    if (fromDevice?.specification_id) {
      terminalSpecIds.add(fromDevice.specification_id)
    }
    const toDevice = devicesById.get(connection.to_instance_id)
    if (toDevice?.device_type === 'cable') {
      cableSpecIds.add(toDevice.specification_id)
    }
    if (toDevice?.specification_id) {
      terminalSpecIds.add(toDevice.specification_id)
    }
  })

  const specsToLoad = new Set<number>([
    ...relaySpecIds,
    ...terminalSpecIds,
    ...cableSpecIds,
  ])

  await Promise.all(
    Array.from(specsToLoad)
      .filter((specId) => specId)
      .map((specId) =>
        fetchDeviceContacts(specId)
          .then((response) => {
        const contacts = toArray<DeviceContact>(response)
        contactsBySpec.set(specId, contacts)
        contacts.forEach((contact) => {
          const id = typeof contact.id === 'string' ? Number(contact.id) : contact.id
          if (!Number.isFinite(id)) {
            return
          }
          const normalizedContact: DeviceContact = {
            ...contact,
            id,
            contact_label: contact.contact_label ?? null,
            contact_position: contact.contact_position ?? null,
            color_code: contact.color_code ?? null,
          }
          contactById.set(id, normalizedContact)
          const colorMap = cableColorsBySpec.get(specId) ?? new Map<number, string | null>()
          colorMap.set(id, normalizedContact.color_code ?? null)
          cableColorsBySpec.set(specId, colorMap)
        })
          })
          .catch(() => {
            contactsBySpec.set(specId, [])
          }),
      ),
  )

  await Promise.all(
    Array.from(terminalSpecIds)
      .filter((specId) => specId)
      .map((specId) =>
        getTerminalInternalConnections(specId)
          .then((response) => {
            const list = toArray<TerminalInternalConnection>(response)
            const map = new Map<number, number>()
            list.forEach((item) => {
              const fromId = Number(item.contact_from_id)
              const toId = Number(item.contact_to_id)
              if (!Number.isFinite(fromId) || !Number.isFinite(toId)) {
                return
              }
              map.set(fromId, toId)
              map.set(toId, fromId)
              terminalOpposites.set(fromId, toId)
              terminalOpposites.set(toId, fromId)
            })
            terminalInternalMaps.set(specId, map)
          })
          .catch(() => {
            terminalInternalMaps.set(specId, new Map())
          }),
      ),
  )

  const connectionsByInstance = new Map<number, ElectricConnection[]>()
  connections.forEach((connection) => {
    const fromList = connectionsByInstance.get(connection.from_instance_id) ?? []
    fromList.push(connection)
    connectionsByInstance.set(connection.from_instance_id, fromList)

    const toList = connectionsByInstance.get(connection.to_instance_id) ?? []
    toList.push(connection)
    connectionsByInstance.set(connection.to_instance_id, toList)
  })

  const context = {
    devicesById,
    devicesByLabel,
    connectionsByInstance,
    contactsBySpec,
    contactById,
    terminalInternalMaps,
    terminalInstanceIds,
    mainSchemaId,
    cableSpecPairs,
    cableColorsBySpec,
  }

  const debugRows: RelayConnectionDebugRow[] = []
  const bottomContacts: RelayBottomContactRow[] = []

  relayDevices.forEach((relayDevice) => {
    const relayLabel = relayDevice.position_label ?? null
    const contacts = contactsBySpec.get(relayDevice.specification_id) ?? []
    const bottom = contacts.filter(
      (contact) => contact.contact_position === 'bottom' || contact.contact_label?.match(/^\d+o$/i),
    )

    bottom.forEach((contact) => {
      const relayConnections = connectionsByInstance.get(relayDevice.id) ?? []
      const connection = relayConnections.find(
        (conn) =>
          (conn.from_instance_id === relayDevice.id && conn.from_contact_id === contact.id) ||
          (conn.to_instance_id === relayDevice.id && conn.to_contact_id === contact.id),
      )

      if (connection) {
        const fromRelay = connection.from_instance_id === relayDevice.id
        const otherInstanceId = fromRelay ? connection.to_instance_id : connection.from_instance_id
        const otherContactId = Number(fromRelay ? connection.to_contact_id : connection.from_contact_id)
        const otherDevice = devicesById.get(otherInstanceId)
        const otherContact = contactById.get(otherContactId)
        const terminalDevice = devicesById.get(otherInstanceId)
        const terminalSpecId = terminalDevice?.specification_id ?? 0
        const internalMap = terminalInternalMaps.get(terminalSpecId)
        let oppositeContactId: number | null = null
        let oppositeContact: DeviceContact | null = null

        const oppositeContactIdValue = terminalOpposites.get(otherContactId) ?? null
        if (oppositeContactIdValue != null && Number.isFinite(oppositeContactIdValue)) {
          oppositeContactId = Number(oppositeContactIdValue)
          oppositeContact = contactById.get(oppositeContactId) ?? null
        }

        if (!oppositeContactId) {
          const label = otherContact?.contact_label ?? ''
          const match = label.match(/^(\d+)([A-Za-z]+)$/)
          if (match) {
            const base = match[1]
            const suffix = match[2].toUpperCase()
            const oppositeSuffix =
              suffix === 'D' ? 'U' :
              suffix === 'U' ? 'D' :
              suffix === 'TOP' ? 'BOTTOM' :
              suffix === 'BOTTOM' ? 'TOP' :
              null

            if (oppositeSuffix) {
              const oppositeContacts = contactsBySpec.get(terminalSpecId) ?? []
              const candidate = oppositeContacts.find((candidateContact) =>
                candidateContact.contact_label?.toUpperCase() === `${base}${oppositeSuffix}`,
              )
              if (candidate) {
                const candidateId = Number((candidate as any).id ?? candidate.id)
                if (Number.isFinite(candidateId)) {
                  oppositeContactId = candidateId
                  oppositeContact = contactById.get(candidateId) ?? candidate
                }
              }
            }
          }
        }

        const terminalConnectionsAll = connectionsByInstance.get(otherInstanceId) ?? []

        const isCableDevice = (device: DeviceInstance | undefined) =>
          device?.device_type === 'cable' ||
          (device?.specification_id ? cableSpecIds.has(device.specification_id) : false)

        const resolveCableConnection = (contactId: number | null) => {
          if (!contactId) return null
          const connection = terminalConnectionsAll.find((conn) => {
            const fromTerminal = conn.from_instance_id === otherInstanceId && conn.from_contact_id === contactId
            const toTerminal = conn.to_instance_id === otherInstanceId && conn.to_contact_id === contactId
            if (!fromTerminal && !toTerminal) {
              return false
            }
            const otherInst = fromTerminal ? conn.to_instance_id : conn.from_instance_id
            const otherDev = devicesById.get(otherInst)
            return isCableDevice(otherDev)
          })
          if (!connection) return null

          const fromTerminal = connection.from_instance_id === otherInstanceId
          const cableInst = fromTerminal ? connection.to_instance_id : connection.from_instance_id
          const cableContact = fromTerminal ? connection.to_contact_id : connection.from_contact_id
          const cableDevice = devicesById.get(cableInst)
          const cableSpecId = cableDevice?.specification_id ?? null
          const cableContacts = cableSpecId ? contactsBySpec.get(cableSpecId) ?? [] : []
          const colorMap = cableSpecId ? cableColorsBySpec.get(cableSpecId) ?? new Map<number, string | null>() : null
          const cableColor = colorMap?.get(cableContact) ?? null
          const cableContactInfo =
            cableContacts.find((item) => Number((item as any).id ?? item.id) === cableContact) ??
            contactById.get(cableContact) ??
            null

          let oppositeCableContactId: number | null = null
          if (cableColor) {
            const alternate = cableContacts.find((item) => {
              const contactId = Number((item as any).id ?? item.id)
              if (!Number.isFinite(contactId) || contactId === cableContact) {
                return false
              }
              return (item.color_code ?? null) === cableColor
            })
            if (alternate) {
              const altId = Number((alternate as any).id ?? alternate.id)
              if (Number.isFinite(altId)) {
                oppositeCableContactId = altId
              }
            }
          }

          const cableConnectionsAll = connectionsByInstance.get(cableInst) ?? []
          let oppositeConnection: ElectricConnection | null = null

          if (oppositeCableContactId != null) {
            const found = cableConnectionsAll.find((conn) => {
              if (conn.id === connection.id) return false
              const fromCable = conn.from_instance_id === cableInst && conn.from_contact_id === oppositeCableContactId
              const toCable = conn.to_instance_id === cableInst && conn.to_contact_id === oppositeCableContactId
              if (!fromCable && !toCable) return false
              const remoteInstanceId = fromCable ? conn.to_instance_id : conn.from_instance_id
              if (remoteInstanceId === otherInstanceId || remoteInstanceId === relayDevice.id) {
                return false
              }
              const remoteDevice = devicesById.get(remoteInstanceId)
              return remoteDevice != null && remoteDevice.device_type !== 'cable'
            })
            if (found) {
              oppositeConnection = found
            }
          }

          if (!oppositeConnection && cableColor) {
            const found = cableConnectionsAll.find((conn) => {
              if (conn.id === connection.id) return false
              const fromCable = conn.from_instance_id === cableInst
              const contactUsed = fromCable ? conn.from_contact_id : conn.to_contact_id
              if (contactUsed === cableContact) return false
              const contactColor = colorMap?.get(contactUsed) ?? null
              if (contactColor !== cableColor) return false
              const remoteInstanceId = fromCable ? conn.to_instance_id : conn.from_instance_id
              if (remoteInstanceId === otherInstanceId || remoteInstanceId === relayDevice.id) {
                return false
              }
              const remoteDevice = devicesById.get(remoteInstanceId)
              if (!remoteDevice || remoteDevice.device_type === 'cable') {
                return false
              }
              if (context.terminalInstanceIds.has(remoteInstanceId)) {
                return false
              }
              return Boolean(remoteDevice.position_label ?? remoteDevice.description)
            })

            if (found) {
              oppositeConnection = found
              const fromCable = found.from_instance_id === cableInst
              oppositeCableContactId = fromCable ? found.from_contact_id : found.to_contact_id
            }
          }

          let remoteInstanceId: number | null = null
          let remoteDeviceLabel: string | null = null
          let remoteSpecName: string | null = null
          let remoteDescription: string | null = null
          if (oppositeConnection) {
            const fromCable = oppositeConnection.from_instance_id === cableInst
            remoteInstanceId = fromCable ? oppositeConnection.to_instance_id : oppositeConnection.from_instance_id
            const remoteDevice = devicesById.get(remoteInstanceId)
            remoteDeviceLabel =
              remoteDevice?.position_label ?? remoteDevice?.description ?? remoteDevice?.spec_name ?? null
            remoteSpecName = remoteDevice?.spec_name ?? null
            remoteDescription = remoteDevice?.description ?? null
          }

          const cableOppositeContactLabel =
            oppositeCableContactId != null
              ? contactById.get(oppositeCableContactId)?.contact_label ?? null
              : null

          return {
            cableConnectionId: connection.id,
            cableInstanceId: cableInst,
            cableContactId: cableContact,
            cableContactLabel: cableContactInfo?.contact_label ?? null,
            cableColor,
            cableOppositeConnectionId: oppositeConnection?.id ?? null,
            cableOppositeInstanceId: remoteInstanceId,
            cableOppositeContactId: oppositeCableContactId,
            cableOppositeContactLabel,
            cableOppositeLabel: remoteDeviceLabel,
            cableOppositeSpecName: remoteSpecName,
            cableOppositeDescription: remoteDescription,
          }
        }

        const cableFromPrimary = resolveCableConnection(otherContactId)
        const cableFromOpposite = !cableFromPrimary ? resolveCableConnection(oppositeContactId) : null

        const cableResult = cableFromPrimary ?? cableFromOpposite ?? {
          cableConnectionId: null,
          cableInstanceId: null,
          cableContactId: null,
          cableContactLabel: null,
          cableColor: null,
          cableOppositeConnectionId: null,
          cableOppositeInstanceId: null,
          cableOppositeContactId: null,
          cableOppositeContactLabel: null,
          cableOppositeLabel: null,
          cableOppositeSpecName: null,
          cableOppositeDescription: null,
        }

        bottomContacts.push({
          relayInstanceId: relayDevice.id,
          relayLabel,
          relayContactId: contact.id,
          relayContactLabel: contact.contact_label ?? null,
          connectionId: connection.id,
          schemaId: connection.schema_id,
          targetInstanceId: otherInstanceId,
          targetContactId: otherContactId,
          targetLabel: otherDevice?.position_label ?? null,
          targetContactLabel: otherContact?.contact_label ?? null,
          targetSpecName: otherDevice?.spec_name ?? null,
          targetDescription: otherDevice?.description ?? null,
          oppositeContactId: oppositeContactId ?? null,
          oppositeContactLabel: oppositeContact?.contact_label ?? null,
          terminalSpecId: terminalSpecId || null,
          hasInternalMap: Boolean(internalMap && internalMap.size > 0),
          cableConnectionId: cableResult.cableConnectionId,
          cableInstanceId: cableResult.cableInstanceId,
          cableContactId: cableResult.cableContactId,
          cableContactLabel: cableResult.cableContactLabel,
          cableColor: cableResult.cableColor ?? null,
          cableOppositeConnectionId: cableResult.cableOppositeConnectionId ?? null,
          cableOppositeInstanceId: cableResult.cableOppositeInstanceId ?? null,
          cableOppositeContactId: cableResult.cableOppositeContactId ?? null,
          cableOppositeContactLabel: cableResult.cableOppositeContactLabel ?? null,
          cableOppositeLabel: cableResult.cableOppositeLabel ?? null,
          cableOppositeSpecName: cableResult.cableOppositeSpecName ?? null,
          cableOppositeDescription: cableResult.cableOppositeDescription ?? null,
        })
      } else {
        bottomContacts.push({
          relayInstanceId: relayDevice.id,
          relayLabel,
          relayContactId: contact.id,
          relayContactLabel: contact.contact_label ?? null,
          connectionId: null,
          schemaId: null,
          targetInstanceId: null,
          targetContactId: null,
          targetLabel: null,
          targetContactLabel: null,
          targetSpecName: null,
          targetDescription: null,
          oppositeContactId: null,
          oppositeContactLabel: null,
          terminalSpecId: null,
          hasInternalMap: false,
          cableConnectionId: null,
          cableInstanceId: null,
          cableContactId: null,
          cableContactLabel: null,
          cableColor: null,
          cableOppositeConnectionId: null,
          cableOppositeInstanceId: null,
          cableOppositeContactId: null,
          cableOppositeContactLabel: null,
          cableOppositeLabel: null,
          cableOppositeSpecName: null,
          cableOppositeDescription: null,
        })
      }
    })
  })

  connections
    .filter((connection) => connection.schema_id === mainSchemaId)
    .forEach((connection) => {
      const fromDevice = devicesById.get(connection.from_instance_id)
      const toDevice = devicesById.get(connection.to_instance_id)
      if (!fromDevice || !toDevice) return
      const fromContact = contactById.get(connection.from_contact_id)
      const toContact = contactById.get(connection.to_contact_id)
      const fromIsRelay =
        fromDevice.position_label?.startsWith('R-') && fromContact?.contact_label?.match(/^\d+o$/i)
      const toIsRelay =
        toDevice.position_label?.startsWith('R-') && toContact?.contact_label?.match(/^\d+o$/i)

      if (!fromIsRelay && !toIsRelay) {
        return
      }

      const relayInstanceId = fromIsRelay ? connection.from_instance_id : connection.to_instance_id
      const relayContactId = fromIsRelay ? connection.from_contact_id : connection.to_contact_id
      const relayLabel = fromIsRelay ? fromDevice.position_label : toDevice.position_label
      const relayContactLabel = fromIsRelay ? fromContact?.contact_label ?? null : toContact?.contact_label ?? null

      const terminalInstanceId = fromIsRelay ? connection.to_instance_id : connection.from_instance_id
      const terminalContactId = fromIsRelay ? connection.to_contact_id : connection.from_contact_id
      const terminalLabel = fromIsRelay ? toDevice.position_label : fromDevice.position_label
      const terminalContactLabel = fromIsRelay ? toContact?.contact_label ?? null : fromContact?.contact_label ?? null

      debugRows.push({
        connectionId: connection.id,
        schemaId: connection.schema_id,
        relayInstanceId,
        relayContactId,
        relayLabel: relayLabel ?? null,
        relayContactLabel,
        terminalInstanceId,
        terminalContactId,
        terminalLabel: terminalLabel ?? null,
        terminalContactLabel,
      })
    })

  debugRows.sort((a, b) => {
    if ((a.relayLabel ?? '') !== (b.relayLabel ?? '')) {
      return (a.relayLabel ?? '').localeCompare(b.relayLabel ?? '')
    }
    if ((a.relayContactLabel ?? '') !== (b.relayContactLabel ?? '')) {
      return (a.relayContactLabel ?? '').localeCompare(b.relayContactLabel ?? '')
    }
    return a.connectionId - b.connectionId
  })

  bottomContacts.forEach((row) => {
    if (!row.relayLabel || !row.relayContactLabel) {
      return
    }
    const channelMatch = row.relayContactLabel.match(/^(\d+)/)
    if (!channelMatch) {
      return
    }
    const channelIndex = Number(channelMatch[1])
    if (!Number.isFinite(channelIndex)) {
      return
    }
    const friendlyName = (row.cableOppositeLabel ?? row.targetLabel ?? '').trim()
    if (!friendlyName) {
      return
    }
    const specName = row.cableOppositeSpecName ?? row.targetSpecName ?? null
    const deviceDescription = row.cableOppositeDescription ?? row.targetDescription ?? null
    const cmdKey = `${row.relayLabel}.CMD.${channelIndex}`
    const fbKey = `${row.relayLabel}.FB.${channelIndex}`
    if (!channelNameMap[cmdKey]) {
      channelNameMap[cmdKey] = friendlyName
    }
    if (!channelNameMap[fbKey]) {
      channelNameMap[fbKey] = friendlyName
    }
    if (!channelDetailsMap[cmdKey]) {
      channelDetailsMap[cmdKey] = { specName, deviceDescription }
    }
    if (!channelDetailsMap[fbKey]) {
      channelDetailsMap[fbKey] = { specName, deviceDescription }
    }
  })

  const relayDefinitions: RelayDefinition[] = []

  relayDevices.forEach((relayDevice) => {
    const relayLabel = relayDevice.position_label
    if (!relayLabel) return
    const labelMatch = relayLabel.match(/^R-(\d+)-(\d+)$/i)
    const area = labelMatch?.[1] ?? '0'
    const line = labelMatch?.[2] ?? '0'

    const relayContacts = contactsBySpec.get(relayDevice.specification_id) ?? []
    const channelNumbers = new Set<number>()
    relayContacts.forEach((contact) => {
      if (!contact.contact_label) return
      const match = contact.contact_label.match(/^(\d+)[io]$/i)
      if (match) {
        channelNumbers.add(Number(match[1]))
      }
    })
    const channelCount =
      channelNumbers.size > 0 ? Math.max(...channelNumbers) : DEFAULT_CHANNEL_COUNT

    relayDefinitions.push({
      relayLabel,
      area,
      line,
      channelCount,
    })

    for (let channel = 1; channel <= channelCount; channel += 1) {
      const resolved = resolveRelayChannelName(relayLabel, channel, context)
      if (resolved) {
        const { label, specName, deviceDescription } = resolved
        const cmdKey = `${relayLabel}.CMD.${channel}`
        const fbKey = `${relayLabel}.FB.${channel}`
        channelNameMap[cmdKey] = label
        channelNameMap[fbKey] = label
        channelDetailsMap[cmdKey] = { specName, deviceDescription }
        channelDetailsMap[fbKey] = { specName, deviceDescription }
      }
    }
  })

  return { channelNameMap, channelDetailsMap, relayDefinitions, debugRows, bottomContacts }
}

export default function KnxSchemaExportPage() {
  const { projectId, schemaId } = useParams<{ projectId: string; schemaId: string }>()

  const projectIdNumber = Number(projectId ?? '')
  const schemaIdNumber = Number(schemaId ?? '')

  const { data: mappingData, isLoading: isMappingLoading } = useQuery({
    queryKey: ['knx-relay-mapping', projectIdNumber, schemaIdNumber],
    enabled: Number.isFinite(projectIdNumber) && Number.isFinite(schemaIdNumber),
    queryFn: () => loadRelayChannelMappings(projectIdNumber, schemaIdNumber),
  })

  const channelNameMap = mappingData?.channelNameMap ?? {}
  const channelDetailsMap = mappingData?.channelDetailsMap ?? {}
  const relayDefinitions = mappingData?.relayDefinitions ?? []

  const tableRows = useMemo(
    () => buildKnxRows(relayDefinitions, channelNameMap, channelDetailsMap),
    [relayDefinitions, channelNameMap, channelDetailsMap],
  )

  const homeAssistantConfig = useMemo(() => {
    if (tableRows.length === 0) {
      return 'switch:\n  # No KNX relay channels available'
    }

    const lines: string[] = ['switch:']
    tableRows
      .filter((row) => row.channel && row.channel.includes('.CMD.'))
      .forEach((row) => {
        const channelMatch = row.channel?.match(/^(R-\d+-\d+)\.CMD\.(\d+)(?:\.\s*(.*))?$/)
        if (!channelMatch) {
          return
        }
        const relayLabel = channelMatch[1]
        const channelNumber = channelMatch[2]
        const friendlyName = (channelMatch[3] ?? '').trim()
        const detailKey = `${relayLabel}.CMD.${channelNumber}`
        const detail = channelDetailsMap[detailKey]
        const description = detail?.deviceDescription?.trim()

        const nameSegments: string[] = [`${relayLabel}.${channelNumber}`]
        if (friendlyName) {
          nameSegments.push(friendlyName)
        }
        if (description) {
          nameSegments.push(description)
        }
        const name = nameSegments.join('. ')

        const [area = '', line = '', group = ''] = (row.groupAddress ?? '').split('/')
        if (!area || !line || !group) {
          return
        }
        const commandAddress = `${area}/${line}/${group}`
        const stateChannel = Number(group)
        if (!Number.isFinite(stateChannel)) {
          return
        }
        const stateAddress = `${area}/${line}/${stateChannel + FEEDBACK_OFFSET}`

        lines.push(`  - name: "${name}"`)
        lines.push(`    address: "${commandAddress}"`)
        lines.push(`    state_address: "${stateAddress}"`)
      })

    if (lines.length === 1) {
      lines.push('  # No mapped relay command channels found')
    }

    return lines.join('\n')
  }, [tableRows, channelDetailsMap])

  const handleExportCsv = () => {
    const csvRows = tableRows.map((row) => {
      const [areaId = '', lineId = '', groupId = ''] = (row.groupAddress ?? '').split('/')
      const values: (string | number | null | undefined)[] = [
        '',
        '',
        row.channel,
        areaId,
        lineId,
        groupId,
        row.comment,
        row.extra,
        row.description,
        row.datapoint,
        row.mode,
      ]
      return values
        .map((value, index) =>
          index === 2 || index === 8 ? toCsvValue(value, true) : toCsvValue(value),
        )
        .join(';')
    })

    const csvContent = csvRows.join('\r\n')
    const encoded = encodeWindows1251(csvContent)
    const blob = new Blob([encoded], { type: 'text/csv;charset=windows-1251;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const safeProjectId = projectId ?? 'project'
    const safeSchemaId = schemaId ?? 'schema'
    link.href = url
    link.setAttribute('download', `knx-schema-${safeProjectId}-${safeSchemaId}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={`/projects/${projectId}`}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            ← Back to project
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900 mt-2">KNX schema overview</h1>
          <p className="text-gray-600 text-sm mt-1">
            Project ID: {projectId} · Schema ID: {schemaId}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isMappingLoading && <span className="text-sm text-gray-500">Auto mapping…</span>}
          <button
            type="button"
            onClick={handleExportCsv}
            className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700 transition-colors"
          >
            Export CSV (semicolon)
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">KNX group addresses</h2>
          <span className="text-sm text-gray-500">Total records: {tableRows.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {KNX_COLUMN_DEFINITIONS.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tableRows.map((row, rowIndex) => (
                <tr key={`${row.range}-${row.channel}-${rowIndex}`} className="hover:bg-gray-50">
                  {KNX_COLUMN_DEFINITIONS.map((column) => (
                    <td
                      key={column.key}
                      className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 ${
                        column.monospace ? 'font-mono' : ''
                      }`}
                    >
                      {row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Home Assistant configuration</h2>
          <p className="text-sm text-gray-500 mt-1">
            Скопируй блок ниже в YAML. Каналы добавлены только для команд (CMD), состояние берётся из адреса обратной связи (FB).
          </p>
        </div>
        <div className="p-4">
          <textarea
            readOnly
            className="w-full h-64 font-mono text-sm text-gray-900 border border-gray-200 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={homeAssistantConfig}
          />
        </div>
      </div>
    </div>
  )
}
